import React, { useEffect, useRef } from 'react';
import { Button } from '../kit';
import { groupState, isAllDepartments, toggleDepartmentGroup } from './userModel';

function Check({ state, onChange, label, disabled, title, strong }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = state === 'some'; }, [state]);
  return (
    <label className={`us-check-row${strong ? ' us-strong' : ''}`} title={title}>
      <input
        ref={ref}
        type="checkbox"
        className="ak-check"
        checked={state === 'all'}
        disabled={disabled}
        onChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * Grouped department checklist. Stored value semantics match the backend:
 * [] (or any list containing -1) = all departments. Everything is checked
 * in the "all" state; unchecking narrows to an explicit list; checking the
 * last one collapses back to all. The last checked department can't be
 * unchecked — an empty list would silently mean "all".
 *
 *   groups [{ key, label, ids, items?: [{ key, label, ids }] }]
 */
export default function DepartmentEditor({ title, groups, ids, onChange, onDone, loading, emptyText }) {
  const allIds = groups.flatMap((g) => g.ids);
  const all = isAllDepartments(ids);
  const selectedCount = all ? allIds.length : allIds.filter((id) => ids.some((x) => String(x) === String(id))).length;
  const toggle = (groupIds) => onChange(toggleDepartmentGroup(ids, groupIds, allIds));
  const isLastSelection = (groupIds) => !all && groupState(ids, groupIds) === 'all' && selectedCount === groupIds.length;

  return (
    <div className="us-dept-editor" role="group" aria-label={title}>
      <div className="us-dept-editor-head">
        <span className="ak-muted">{all ? 'All departments — uncheck to limit' : `${selectedCount} of ${allIds.length} selected`}</span>
        <span className="ak-foot-spacer" />
        {!all ? <Button size="small" variant="ghost" onClick={() => onChange([])}>Select all</Button> : null}
        {onDone ? <Button size="small" onClick={onDone}>Done</Button> : null}
      </div>
      {loading ? <div className="ak-muted us-pad">Loading departments…</div> : null}
      {!loading && !groups.length ? <div className="ak-muted us-pad">{emptyText || 'No departments set up for this organization.'}</div> : null}
      {!loading && groups.length ? (
        <div className="us-checklist">
          {groups.map((g) => (
            <div key={g.key} className="us-check-group">
              <Check
                strong={Boolean(g.items)}
                state={groupState(ids, g.ids)}
                label={g.items ? `${g.label} (${g.items.length})` : g.ids.length > 1 ? `${g.label} · ${g.ids.length} locations` : g.label}
                disabled={isLastSelection(g.ids)}
                title={isLastSelection(g.ids) ? 'Keep at least one department, or use Select all' : undefined}
                onChange={() => toggle(g.ids)}
              />
              {g.items ? (
                <div className="us-check-children">
                  {g.items.map((it) => (
                    <Check
                      key={it.key}
                      state={groupState(ids, it.ids)}
                      label={it.label}
                      disabled={isLastSelection(it.ids)}
                      title={isLastSelection(it.ids) ? 'Keep at least one department, or use Select all' : undefined}
                      onChange={() => toggle(it.ids)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
